import { Helmet } from "react-helmet-async"

import { DEFAULT_OG_IMAGE_PATH, SITE_NAME, toAbsoluteSiteUrl } from "./seoConfig"

export type StructuredData = Record<string, unknown>
export type RobotsPolicy = "index, follow" | "noindex, follow" | "noindex, nofollow"

type PageSeoProps = {
  title: string
  description: string
  canonicalPath: string
  robots?: RobotsPolicy
  image?: string
  type?: "website" | "product"
  jsonLd?: StructuredData | StructuredData[]
}

const serializeStructuredData = (schema: StructuredData) => JSON.stringify(schema)
  .replace(/</g, "\\u003c")
  .replace(/>/g, "\\u003e")
  .replace(/&/g, "\\u0026")

const renderStructuredDataTags = (schemas: StructuredData[]) => schemas.map((schema, index) => (
  <script key={`${String(schema["@type"] ?? "schema")}-${index}`} type="application/ld+json">
    {serializeStructuredData(schema)}
  </script>
))

const PageSeo = ({
  title,
  description,
  canonicalPath,
  robots = "index, follow",
  image = DEFAULT_OG_IMAGE_PATH,
  type = "website",
  jsonLd,
}: PageSeoProps) => {
  const canonicalUrl = toAbsoluteSiteUrl(canonicalPath)
  const imageUrl = toAbsoluteSiteUrl(image)
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:locale" content="ru_RU" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={title} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={title} />

      {renderStructuredDataTags(schemas)}
    </Helmet>
  )
}

export const StructuredDataScript = ({ data }: { data?: StructuredData | StructuredData[] }) => {
  if (!data) return null
  const schemas = Array.isArray(data) ? data : [data]

  return (
    <Helmet>
      {renderStructuredDataTags(schemas)}
    </Helmet>
  )
}

export default PageSeo
